import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { ChatCircleDots, Sparkle } from "@phosphor-icons/react";
import { useMemo } from "react";
import useEsgAdminFeaturesVisible from "hooks/useEsgAdminFeaturesVisible";
import FoundationDataTable from "./FoundationDataTable";
import { compareWithCurrent, REVIEW_STATE } from "../../../../utils/foundationReviewCompare";

/**
 * What one document was read as, before any of it is kept.
 *
 * A modal rather than an inline expansion: a year can hold several documents of a hundred metrics each, and
 * opening one inline pushed the Foundation Data table off the screen — the thing the reader is comparing
 * against. It also makes the moment explicit. Nothing here has been applied yet, which is the single most
 * important thing for the reader to understand, so the eyebrow says so before the numbers do.
 *
 * The table is the same `FoundationDataTable` the page uses — the Core/Custom tabs, the target structure's
 * sections, the same rows and (i) details — in `reviewMode`: read-only, with gaps hidden. Gaps are hidden
 * rather than shown because this is what ONE document said: sixty gaps against a document that reports four
 * datapoints would read as that document having failed, when it is doing exactly what was expected. Without
 * a target structure it falls back to the full eight-column table, grouped by the agent's categories.
 */
export default function ExtractionReviewModal({
  isOpen,
  onClose,
  run,
  rows,
  canEdit,
  preview,
  isPreviewing,
  isAccepting,
  keepOffSchema,
  onKeepOffSchemaChange,
  // The workstream's target structure. With it the table is the same Core/Custom view as the page.
  structure = null,
  // The workstream's current figures, to show beside each extracted value what it would replace.
  currentRows = null,
  // Extracted-row ids crossed out for this ingest, and the toggle (absent when the user cannot edit).
  ignoredIds = [],
  onToggleIgnored,
  onShowRow,
  onAccept,
  onChat,
}) {
  const comparisons = useMemo(() => compareWithCurrent(rows, currentRows), [rows, currentRows]);
  // ESG-Admin only, and honours the header's hide-admin-features toggle — same gate as the page's Chat with data.
  const showAdminFeatures = useEsgAdminFeaturesVisible();
  if (!run) return null;

  const file = run.sourceFileName || "this document";
  /*
    Crossed-out rows are marked on screen only — Accept still writes them until the backend supports per-row
    exclusion (see the TODO in SourcesPanel's SourceReview). So they are NOT taken out of these counts: the
    counts must describe what Accept will actually do.
  */
  const ignoredSet = new Set(ignoredIds);
  const counts = { [REVIEW_STATE.NEW]: 0, [REVIEW_STATE.MATCHES]: 0, [REVIEW_STATE.DIFFERS]: 0 };
  comparisons.forEach((c) => {
    counts[c.state] += 1;
  });
  /*
    How many current values Accept actually CHANGES. The server's rowsSuperseded also counts values that are
    re-written with the same number (a re-review), which reads as a change when nothing moves — so the display
    uses the "differs" count. Capped at the server's figure, which is the one that honours the keep/leave-out
    choices. Display only: rowsSuperseded is still what Accept sends back as expectedReplacedRows.
  */
  const changedCount = Math.min(counts[REVIEW_STATE.DIFFERS], preview?.rowsSuperseded ?? 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pt={7} px={8} pb={4} borderBottom="1px solid" borderColor="gray.200">
          <Text
            fontSize="12px"
            fontWeight="700"
            letterSpacing="0.06em"
            color="limeShade.600"
            textTransform="uppercase"
          >
            AI extraction · review before applying
          </Text>
          <Text fontSize="heading.l" fontWeight="700" color="darkTeal" mt={1}>
            {rows.length} datapoint{rows.length === 1 ? "" : "s"} found in {file}
          </Text>
          <Text fontSize="14px" color="gray.500" fontWeight="400" mt={2} maxW="720px">
            These values were read out of this document and have not been added to Foundation Data yet.
            Review them before applying — anything your target structure asks for that this document does not
            report is left blank.
          </Text>
          {/* The three review states (compareWithCurrent), counted before the reader scrolls. Matches are
              the reassuring case — especially on a re-review of the same document. */}
          {comparisons.size > 0 ? (
            <HStack spacing={2} mt={3} flexWrap="wrap">
              {/* Chip colours match the row marks: new rows are unmarked (grey), matches carry the green bar,
                  differences the orange one. */}
              <StateCount n={counts[REVIEW_STATE.NEW]} label="new" colorScheme="gray" />
              <StateCount n={counts[REVIEW_STATE.MATCHES]} label="match current values" colorScheme="green" />
              <StateCount n={counts[REVIEW_STATE.DIFFERS]} label="differ from current values" colorScheme="orange" />

            </HStack>
          ) : null}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody px={8} pt={2}>
          <FoundationDataTable
            rows={rows}
            canEdit={false}
            structure={structure}
            onShowUsage={onShowRow}
            reviewMode
            comparisons={comparisons}
            ignoredIds={ignoredSet}
            onToggleIgnored={onToggleIgnored}
          />
        </ModalBody>

        <ModalFooter
          flexDirection="column"
          alignItems="stretch"
          gap={3}
          px={8}
          py={5}
          borderTop="1px solid"
          borderColor="gray.200"
        >
          {/* What Accept will do, from a dry run fired when the modal opened. Stated before the click rather
              than after it, because "replaces 3" is the part a reader would want to stop on. Same compact
              notice as the page's pending-changes banner. */}
          {/* Dev note on the interface: the per-row cross is not wired to the backend yet. Shown only once a row
              is crossed out, so nobody believes it changes what Accept writes. */}
          {ignoredSet.size > 0 ? (
            <Notice tone="orange">
              <Text fontSize="13px" color="gray.700">
                <Text as="span" fontWeight="700" color="orange.800">
                  In development:
                </Text>{" "}
                ignoring individual rows is not active yet — Accept will still add the {ignoredSet.size} crossed-out
                row{ignoredSet.size === 1 ? "" : "s"}. The dev team need to add per-row exclusion to the accept
                endpoint.
              </Text>
            </Notice>
          ) : null}

          {canEdit && preview ? (
            <Notice tone={changedCount > 0 ? "orange" : "gray"}>
              <Text fontSize="14px" color="gray.700">
                Accepting adds{" "}
                <Text as="span" fontWeight="700" color="darkTeal">
                  {preview.rowsWritten} metric{preview.rowsWritten === 1 ? "" : "s"}
                </Text>{" "}
                to Foundation Data
                {changedCount > 0 ? (
                  <>
                    , changing{" "}
                    <Text as="span" fontWeight="700" color="darkTeal">
                      {changedCount}
                    </Text>{" "}
                    already there. Replaced values are archived, not deleted.
                  </>
                ) : (
                  "."
                )}
              </Text>
            </Notice>
          ) : null}

          {/* Only when there ARE off-schema figures — a choice offered where nothing turns on it is noise, and
              teaches the reader to click past the one that matters. These are the Custom tab's rows. */}
          {canEdit && preview?.rowsOffSchema > 0 ? (
            <Notice tone="orange">
              <Flex align="center" gap={4} wrap="wrap">
                <Text fontSize="14px" color="gray.700" flex="1" minW="240px">
                  <Text as="span" fontWeight="700" color="darkTeal">
                    {preview.rowsOffSchema} custom figure{preview.rowsOffSchema === 1 ? "" : "s"}
                  </Text>{" "}
                  match no datapoint in your target structure.
                </Text>
                <ChoiceToggle
                  keep={keepOffSchema}
                  onChange={onKeepOffSchemaChange}
                  keepLabel="Keep in Custom"
                  dropLabel="Leave out"
                />
              </Flex>
            </Notice>
          ) : null}

          <HStack justify="flex-end" spacing={3} pt={1}>
            {isPreviewing ? (
              <HStack spacing={2} mr="auto" color="gray.500">
                <Spinner size="xs" />
                <Text fontSize="xs">Checking what this would change…</Text>
              </HStack>
            ) : null}

            <Button variant="secondary" onClick={onClose}>
              {canEdit ? "Discard" : "Close"}
            </Button>

            {canEdit ? (
              <>
                {showAdminFeatures ? (
                  <Button variant="secondary" leftIcon={<ChatCircleDots size={16} />} onClick={onChat}>
                    Chat about this
                  </Button>
                ) : null}
                <Button
                  variant="primary"
                  leftIcon={<Sparkle size={16} />}
                  onClick={onAccept}
                  isLoading={isAccepting}
                  isDisabled={rows.length === 0}
                >
                  Accept
                </Button>
              </>
            ) : null}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/** A compact notice in the style of the page's pending-changes banner: white card, coloured left bar. */
function Notice({ tone = "gray", children }) {
  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderLeft="3px solid"
      borderLeftColor={tone === "orange" ? "orange.400" : "gray.300"}
      borderRadius="md"
      px={4}
      py={3}
    >
      {children}
    </Box>
  );
}

/** Keep / leave-out as a two-option segmented control rather than two stacked radio lines. */
function ChoiceToggle({ keep, onChange, keepLabel, dropLabel }) {
  const option = (value, label) => (
    <Button
      size="xs"
      variant="unstyled"
      px={3}
      h="26px"
      borderRadius="full"
      fontWeight="600"
      fontSize="12px"
      bg={keep === value ? "white" : "transparent"}
      color={keep === value ? "darkTeal" : "gray.500"}
      boxShadow={keep === value ? "sm" : "none"}
      onClick={() => onChange?.(value)}
    >
      {label}
    </Button>
  );
  return (
    <HStack spacing={0} bg="gray.100" borderRadius="full" p="3px" flexShrink={0}>
      {option(true, keepLabel)}
      {option(false, dropLabel)}
    </HStack>
  );
}

function StateCount({ n, label, colorScheme }) {
  if (!n) return null;
  return (
    <Badge variant="subtle" colorScheme={colorScheme} textTransform="none" fontSize="12px" px={2} py="2px">
      {n} {label}
    </Badge>
  );
}
